
<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div
      class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25"
    >
      <div class="d-sm-flex align-items-center">
        <h5 class="text-black fw-semibold mb-0">Monnaies/Quantités</h5>
      </div>
      <div class="d-flex align-items-center">
        <label for="caisse" class="text-black fw-semibold me-2">Caisse :</label>
        <Multiselect
          v-model="caisse"
          :options="caisseOptions"
          :preserve-search="true"
          :multiple="false"
          :searchable="true"
          placeholder="Sélectionner une caisse"
          label="label"
          track-by="value"
          class="form-control shadow-none fs-md-14 text-black"
          style="width: 300px;"
          @change="handleCaisseChange"
        />
      </div>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25">
      <div v-if="caisseOptions.length === 0" class="text-danger fs-md-12">
        Aucune caisse ouverte disponible.
      </div>
      <div v-else class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th scope="col">Monnaie</th>
              <th scope="col">Quantité</th>
              <th scope="col">Montant</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(monnaie, index) in monnaiesDisponibles" :key="index">
              <td>{{ monnaie.libelle }}</td>
              <td>{{ monnaie.qteMonnaie }}</td>
              <td>{{ monnaie.montant }}</td>
            </tr>
            <tr v-if="monnaiesDisponibles.length === 0 && caisse">
              <td colspan="3" class="text-center text-danger fs-md-12">
                Aucune monnaie disponible pour cette ouverture.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="row mt-3">
        <div class="col-md-10"></div> 
        <div class="col-md-2">
          <div class="form-group mb-2">
            <label class="form-label fw-semibold mb-1 text-start d-block">
              Montant Total
            </label>
            <input
              type="number"
              class="form-control form-control-sm shadow-none text-start"
              v-model.number="montantTotal"
              readonly
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref,watch} from "vue";
import Multiselect from "@vueform/multiselect";
import Swal from "sweetalert2";
import ApiService from "@/services/ApiService";
import { error} from "@/utils/utils";

interface MonnaieDisponible {
  monnaieId: number;
  libelle: string;
  valeur: number;
  qteMonnaie: number;
  montant: number;
}

interface CaisseOption {
  value: number;
  label: string;
}

export default defineComponent({
  name: "ListeMonnaiesDisponibles",
  components: {
    Multiselect,
  },
  setup() {
    const caisse = ref<number | null>(null);
    const caisseOptions = ref<CaisseOption[]>([]);
    const monnaiesDisponibles = ref<MonnaieDisponible[]>([]);
    const montantTotal=ref(0);

    const getCaisse = () => {
      ApiService.get("/all/caisses")
        .then(({ data }) => {
          const donnees = data.data.data;
          caisseOptions.value = donnees
            .filter((ca: any) => ca.statut == 1)
            .map((ca: any) => ({
              label: ca.nom,
              value: ca.id,
            }));
          if (caisseOptions.value.length > 0) {
            caisse.value = caisseOptions.value[0].value;
            handleCaisseChange(caisse.value);
          } else {
            Swal.fire({
              text: "Aucune caisse ouverte disponible.",
              toast: true,
              icon: "warning",
              position: "top-right",
              showConfirmButton: false,
              timer: 5000,
              timerProgressBar: true,
              heightAuto: false,
            });
          }
        })
        .catch(({ response }) => {
          error(response?.data?.message || "Erreur lors de la récupération des caisses.");
        });
    };

    const updateMontant = (billetage: MonnaieDisponible) => {
      billetage.montant = billetage.qteMonnaie * billetage. valeur || 0;
      
    };

    const calculateTotalMontant = (billetage: MonnaieDisponible[]) => {
      const total = billetage.reduce((sum, element) => {
        return sum + (element.montant || 0);
      }, 0);
      montantTotal.value = total;
    };
    const handleCaisseChange = (newCaisseId: number) => {
      if (newCaisseId) {
        ApiService.get(`/ouvfers/monnaies-disponibles?caisseId=${newCaisseId}`)
          .then(({ data }) => {
            monnaiesDisponibles.value = data.data || [];
            monnaiesDisponibles.value.forEach(updateMontant);
            calculateTotalMontant(monnaiesDisponibles.value);
          })
          .catch(({ response }) => {
            monnaiesDisponibles.value = [];
             montantTotal.value = 0;
            error(response?.data?.message || "Erreur lors de la récupération des monnaies disponibles.");
          });
      } else {
        monnaiesDisponibles.value = [];
        montantTotal.value = 0;
      }
    };

     watch(monnaiesDisponibles, () => {
      monnaiesDisponibles.value.forEach(updateMontant);
      calculateTotalMontant(monnaiesDisponibles.value);
    }, { deep: true });

    onMounted(() => {
      getCaisse();
    });

    return {
      caisse,
      caisseOptions,
      monnaiesDisponibles,
      handleCaisseChange,
      updateMontant,
      calculateTotalMontant,
     montantTotal
    };
  },
});
</script>
<style scoped>
.card {
  margin-bottom: 25px;
}
.card-head {
  padding: 15px 20px;
}
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
}
.table thead th {
  background-color: #0a59a4;
  color: white;
  font-weight: 500;
}
.text-danger {
  margin-top: 0.5rem;
}
.form-control {
  min-height: 38px;
}
</style>
